import { ToastrService } from 'ngx-toastr';
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ViewUpdateTprApiService } from '../../../../../../modules/api/view-update-tpr-api.service';
import { ViewUpdateTpr } from '../../view-update-tpr.class';
import { defaultIfEmpty, map, finalize, filter } from 'rxjs/operators';
import { TprecmAuthorizationService } from '../../../authorization/tprecm-authorization.service';

const _green = 'Green';
const _rWoP = 'R w/o P';
const _rWP = 'R w P';
const _greenClass = 'Green';
const _redClass = 'Red';
const _defaultClass = 'Default';

enum RiskType {
    GREEN = 'Green',
    RWOP = 'R w/o P',
    RWP = 'R w P'
}

interface Source {
    designSources?: any[];
    constSources?: any[];
    firstUserSources?: any[];
    userSources?: any[];
    fixtureLocs?: any[];
};

@Component({
    selector: 'tpr-summary',
    templateUrl: './tpr-summary.template.html',
    styleUrls: ['../../../tprecm.style.scss', './tpr-summary.scss']
})
export class TprSummaryComponent implements OnInit {

    disabled = false;
    _selectedTpr: ViewUpdateTpr;

    get selectedTpr(): ViewUpdateTpr {
        return this._selectedTpr;
    }

    @Input()
    set selectedTpr(value: ViewUpdateTpr) {
        this.disabled = false;
        if (value) {
            this._selectedTpr = value;
        } else {
            this.disabled = true;
            this._selectedTpr = new ViewUpdateTpr();
        }
    }

    @Input()
    reSourced = false;

    @Input()
    inActive = false;

    RiskType = RiskType;

    daysOpts: any[] = Array(41).fill(0).map((_, index) => -20 + index);
    riskFirstUser = _green; // _green, _rWoP, _rWP
    riskFirstUserCheck = false;
    riskFirstUserClass;
    sources: Source = {};

    @Input()
    set lists(value: Source) {
        if (value) {
            this.sources = value || {};
        }
    }

    @Output()
    updateEvent = new EventEmitter<any>();

    ngOnInit() {
        this.riskFirstUserClass = _defaultClass;
        if (this.riskFirstUser === _green) {
            this.riskFirstUserClass = _greenClass;
        }
        else {
            if (this.riskFirstUser === _rWoP || this.riskFirstUser === _rWP) {
                this.riskFirstUserClass = _redClass;
            }
        }

    }

    constructor(
        private readonly viewUpdateTprApiService: ViewUpdateTprApiService,
        private readonly toastr: ToastrService,
        private readonly auth: TprecmAuthorizationService
    ) { }

    onRiskFirstUserCheckChange() {
        this.onGenericFieldChange('r1', this.selectedTpr.r1, false);
    }

    onDieTypeChange() {
        this.onGenericFieldChange('progMake', this.selectedTpr.progMake, false);
    }

    onBuildActualFinishChanged() {
        const start = new Date(this.selectedTpr.buildActualStart);
        start.setHours(0, 0, 0);

        if (this.selectedTpr.buildActualFinish && this.selectedTpr.buildActualFinish < start) {
            this.toastr.error('Date not saved. The Actual Date must be greater than or equal to the Actual Start date.');
            return;
        }

        this.onGenericFieldChange('buildActualFinish', this.selectedTpr.buildActualFinish || '', true);
    }

    onDesignActualFinishChanged() {
        const start = new Date(this.selectedTpr.designActualStart);
        start.setHours(0, 0, 0);

        if (this.selectedTpr.designActualFinish && this.selectedTpr.designActualFinish < start) {
            this.toastr.error('Date not saved. The Actual Date must be greater than or equal to the Actual Start date.');
            return;
        }

        this.onGenericFieldChange('designActualFinish', this.selectedTpr.designActualFinish || '', false);
    }

    fieldAuthorized(field: string) {
        return this.auth.getAuthorizationByField(field);
    }

    onGenericFieldChange(fieldName: string, fieldValue: any, calcFieldsRequired: boolean) {

        this.disabled = true;
        const params = {
            toolId: this.selectedTpr.toolId,
            tprToolId: this.selectedTpr.tprToolId,
            calcFieldsRequired,
            fieldName,
            fieldValue
        };
        this.viewUpdateTprApiService.updateGenericTprField(params)
            .pipe(
                filter(Boolean),
                defaultIfEmpty({ data: [], totalCount: 0 }),
                map((response: any) => response.data[0]),
                finalize(() => this.disabled = false)
            ).subscribe(
                (updatedTpr: any) => this.updateEvent.emit(updatedTpr),
                (err: Error) => this.updateEvent.emit()
            );
    }

    firstUserDateEnabled() {
        return (this.selectedTpr.arrvTryout || this.selectedTpr.firstUserSource === 'NONE') &&
            (this.selectedTpr.rStudyActualDate || this.selectedTpr.na1);
    }

    getRisk1Calc() {
        return this.selectedTpr.risk1Calc ?
            this.selectedTpr.r1 ? RiskType.RWP : this.selectedTpr.risk1Calc :
            '';
    }

    getEtac() {
        return [
            'ETAC Inside',
            'CTAC',
            'Mini ETAC',
            'ETAC',
            'ITAC'
        ].some(val => val === this.selectedTpr.typeName);
    }

    getTci() {
        return this.selectedTpr.designSource === 'TCI';
    }

    dieTypeEnabled() {
        return ['Single Part', 'White Light Dedi', 'White Light Univ', 'W/L Ded w/EDC',
            'Blue Light Dedi', 'Blue Light Univ', 'W/L Univ w/EDC'].some(typeName => typeName === this.selectedTpr.typeName);
    }

    designActualFinishEnabled() {
        return [
            this.selectedTpr.modelPerc,
            this.selectedTpr.mathPerc,
            this.selectedTpr.detail1Perc,
            this.selectedTpr.checkedPerc,
            this.selectedTpr.opsPerc
        ].every(val => {
            return val === 100;
        });
    }

    buildActualFinishEnabled() {
        return [
            this.selectedTpr.fabricationPerc,
            this.selectedTpr.ncPrgmPerc,
            this.selectedTpr.machiningPerc,
            this.selectedTpr.assemblyPerc,
            this.selectedTpr.certPerc,
            this.selectedTpr.finalAsmPerc
        ].every(val => {
            return val === 100;
        });
    }
}
